import React from 'react'
import { LegalHeader } from './LegalHeader'

export function TermsPage() {
  return (
    <div className="min-h-screen bg-lynx text-dusk">
      <LegalHeader />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-semibold mb-2">Terms of Use</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString()}</p>

        <p className="mb-4">
          Welcome to nura, a meal planning and preparation service that uses AI to help you create
          personalized weekly plans, shopping lists, and prep guidance. By accessing or using nura,
          you agree to these Terms of Use (the “Terms”). If you do not agree, do not use the Service.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">1. Service Overview</h2>
        <p className="mb-4">
          nura provides a web application for meal planning and related features, including AI‑assisted
          recommendations, recipe browsing, PDF plan downloads, and subscription management. We integrate
          with best-in-class third‑party providers for payment processing, email delivery, and advanced
          AI technologies. Certain features require an active subscription.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">2. Eligibility & Accounts</h2>
        <p className="mb-4">
          You must be at least 13 years old (or the minimum age in your jurisdiction) to use the Service.
          You are responsible for your account credentials and all activity under your account. Promptly
          notify us of any unauthorized use.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">3. Subscriptions, Trials, and Billing</h2>
        <p className="mb-2">
          Paid features are offered on a subscription basis with weekly or monthly billing depending on your
          selection. We use secure, industry-standard payment processors to handle all transactions.
          Free trials, if offered, are time‑limited and convert to paid subscriptions unless you cancel before
          the trial ends.
        </p>
        <p className="mb-2">
          You can cancel at any time from your account settings; you will retain access through the end of
          the current billing period. Unless stated otherwise, fees are non‑refundable. Any issues related to
          payment processing, chargebacks, or taxes may be handled by the applicable processor under their
          terms.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">4. AI‑Assisted Content</h2>
        <p className="mb-4">
          Our AI features generate suggestions based on your inputs and preferences. AI output may be
          inaccurate or incomplete; you should independently verify any nutritional, medical, or allergen
          information and ensure recipes meet your dietary needs. nura does not provide medical advice.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">5. Acceptable Use</h2>
        <p className="mb-2">You agree not to:</p>
        <ul className="list-disc pl-6 space-y-1 mb-4 text-dusk">
          <li>Use the Service for unlawful purposes or to infringe others’ rights.</li>
          <li>Attempt to access or tamper with non‑public areas or systems.</li>
          <li>Reproduce, resell, or misrepresent AI outputs as medical or nutritional advice.</li>
          <li>Upload content that is harmful, abusive, or violates third‑party rights.</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-2">6. Intellectual Property</h2>
        <p className="mb-4">
          The Service, including its UI, designs, text, and code, is owned by nura or its licensors. You may
          not copy, reverse engineer, or create derivative works except as permitted by law.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">7. Third‑Party Services</h2>
        <p className="mb-4">
          We collaborate with carefully selected technology partners for payment processing, email delivery, 
          analytics, and AI capabilities. Your use of the Service may involve these third-party services, 
          which are subject to their own terms and privacy policies.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">8. Privacy</h2>
        <p className="mb-4">
          Please see our <a className="text-warning-700 underline" href="/legal/privacy">Privacy Policy</a> for
          how we collect, use, and share information when you use the Service, including analytics and AI
          processing.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">9. Disclaimers</h2>
        <p className="mb-4">
          THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” To the fullest extent permitted by law, we
          disclaim all warranties, express or implied, including accuracy, fitness for a particular purpose,
          and non‑infringement. We do not guarantee any health, nutritional, or budget outcomes.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">10. Limitation of Liability</h2>
        <p className="mb-4">
          To the maximum extent permitted by law, nura and its affiliates will not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or loss of data, profits, or revenues,
          arising from your use of the Service.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">11. Indemnification</h2>
        <p className="mb-4">
          You agree to defend, indemnify, and hold harmless nura from any claims arising out of your use of
          the Service, your content, or your violation of these Terms or applicable law.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">12. Changes to the Service or Terms</h2>
        <p className="mb-4">
          We may modify the Service or these Terms at any time. If we make material changes, we will provide
          reasonable notice (e.g., in‑app or by email). Your continued use after changes become effective
          constitutes acceptance.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">13. Governing Law</h2>
        <p className="mb-4">
          These Terms are governed by the laws of your place of residence unless otherwise required by law.
          Any disputes will be resolved in the courts with jurisdiction over our principal place of business,
          unless otherwise required by applicable law.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">14. Contact</h2>
        <p className="mb-4">
          Questions? Contact us at <a className="text-warning-700 underline" href="mailto:support@nura.app">support@nura.app</a>.
        </p>

        <p className="mt-10 text-xs text-muted-foreground">
          This template is provided for convenience only and does not constitute legal advice. Consult your
          legal counsel to tailor these Terms for your jurisdiction and business.
        </p>
      </div>
    </div>
  )
}
